// src/components/OpenAPISchemeManagement.js
// API & outils — /openapi-schemes. The account's OpenAPI tools: each one is a
// schema whose operations (operationId) the AI agents can call during a
// conversation. List view: header → « À vérifier » (a tool the agent cannot
// call: no operationId, no server address) → tiles (tools, callable
// operations, tools used by an agent) → the tools. A tool (?tool=<id>, or
// ?tool=new): its definition, the schema editor, its credentials (never shown
// back: a blank field keeps the stored value, merge_api_keys), the agents that
// use it and the operation tester.
// API: GET /api/users/user, GET /api/ai-assistants/openapi-schemas (api_keys
// stripped), GET …/{id}/operations, POST / PUT …/{id} {title, description,
// schema_content, api_keys}, DELETE …/{id}, POST …/invoke {operation_id,
// parameters}; GET /api/ai-assistants (read only: selected_tool_ids holds
// « {schema_id}_{operationId} » for the agents that use a tool).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import yaml from 'js-yaml';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import {
    Bot, ChevronRight, CodeXml, FileCode, KeyRound, Play, Plus, Server, Trash2, TriangleAlert, Wrench, Zap,
} from 'lucide-react';
import {
    Badge, Banner, Card, ConfirmDialog, EmptyState, Field, Kpi, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import {
    credentialFields, formatCount, formatShortDay, securitySchemesOf, toolServers, toolsAttention, toolsSummary, toolUsage,
} from './dataToolsModel';
import './OpenAPISchemaManagement.css';

const editorOptions = {
    minimap: { enabled: false },
    fontSize: 13,
    lineNumbers: 'on',
    scrollBeyondLastLine: false,
    automaticLayout: true,
    fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
    padding: { top: 12, bottom: 12 },
    smoothScrolling: true,
    renderLineHighlight: 'line',
    overviewRulerBorder: false,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    scrollbar: {
        vertical: 'visible', horizontal: 'visible', useShadows: false, verticalScrollbarSize: 10, horizontalScrollbarSize: 10,
    },
};

const METHOD_TONE = { get: 'info', post: 'success', put: 'warning', patch: 'warning', delete: 'danger' };

const OpenAPISchemaManagement = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toolParam = searchParams.get('tool');

    const [user, setUser] = useState(undefined);
    const [schemas, setSchemas] = useState(null); // null = loading
    const [assistants, setAssistants] = useState(null);
    const [selectedSchema, setSelectedSchema] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [schemaContent, setSchemaContent] = useState('');
    const [operations, setOperations] = useState([]);
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [parameters, setParameters] = useState({});
    const [response, setResponse] = useState(null);
    const [testError, setTestError] = useState(null);
    const [apiKeys, setApiKeys] = useState({});
    const [saving, setSaving] = useState(false);
    const [invoking, setInvoking] = useState(false);
    const [deleting, setDeleting] = useState(null); // schema awaiting confirmation
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);

    const showToast = (tone, text) => setToast({ tone, text });
    useEffect(() => {
        if (!toast || toast.tone === 'danger') return undefined;
        const timer = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(timer);
    }, [toast]);

    const parsedSchema = useMemo(() => {
        if (!schemaContent.trim()) return null;
        try {
            return yaml.load(schemaContent);
        } catch (parseError) {
            return null;
        }
    }, [schemaContent]);

    const securitySchemes = useMemo(() => securitySchemesOf(parsedSchema), [parsedSchema]);
    const credentials = useMemo(() => credentialFields(securitySchemes), [securitySchemes]);

    const fetchUserData = useCallback(async (token) => {
        try {
            const res = await axios.get('/api/users/user', { headers: { Authorization: `Bearer ${token}` } });
            setUser(res.data);
        } catch (fetchError) {
            console.error('Failed to fetch user data:', fetchError);
            setError(t('dataToolsUi.tools.userError', 'Impossible de charger votre profil.'));
            setUser(null);
            setSchemas((previous) => previous || []);
        }
    }, [t]);

    const fetchSchemas = useCallback(async (token) => {
        try {
            const res = await axios.get('/api/ai-assistants/openapi-schemas', { headers: { Authorization: `Bearer ${token}` } });
            setSchemas(Array.isArray(res.data) ? res.data : []);
        } catch (fetchError) {
            console.error('Failed to fetch schemas:', fetchError);
            setSchemas((previous) => previous || []);
            setError(t('dataToolsUi.tools.loadError', 'Impossible de charger vos outils.'));
        }
    }, [t]);

    const fetchOperations = useCallback(async (schemaId, token) => {
        try {
            const res = await axios.get(`/api/ai-assistants/openapi-schemas/${schemaId}/operations`, { headers: { Authorization: `Bearer ${token}` } });
            setOperations(Array.isArray(res.data) ? res.data : []);
        } catch (fetchError) {
            console.error('Failed to fetch operations:', fetchError);
            setOperations([]);
            setError(t('dataToolsUi.tools.operationsError', 'Impossible de lire les opérations de cet outil.'));
        }
    }, [t]);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }
        fetchUserData(token);
    }, [fetchUserData, navigate]);

    useEffect(() => {
        if (!user) return;
        const token = localStorage.getItem('access_token');
        fetchSchemas(token);
        axios.get('/api/ai-assistants', { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setAssistants(Array.isArray(res.data) ? res.data : null))
            .catch(() => setAssistants(null));
    }, [fetchSchemas, user]);

    // The credential fields follow the schema's security schemes.
    useEffect(() => {
        setApiKeys((currentKeys) => {
            const nextKeys = {};
            credentials.forEach((field) => { nextKeys[field.key] = currentKeys[field.key] || ''; });
            return nextKeys;
        });
    }, [credentials]);

    const resetForm = useCallback(() => {
        setSelectedSchema(null);
        setTitle('');
        setDescription('');
        setSchemaContent('');
        setOperations([]);
        setSelectedOperation(null);
        setParameters({});
        setResponse(null);
        setTestError(null);
        setApiKeys({});
        setError('');
    }, []);

    // ?tool=<id> opens that tool, ?tool=new a blank one.
    useEffect(() => {
        if (!toolParam || schemas === null) return;
        if (toolParam === 'new') {
            if (selectedSchema) resetForm();
            return;
        }
        if (selectedSchema?._id === toolParam) return;
        const schema = schemas.find((s) => s._id === toolParam);
        if (!schema) return;
        setSelectedSchema(schema);
        setTitle(schema.title || '');
        setDescription(schema.description || '');
        setSchemaContent(schema.schema_content || '');
        setApiKeys(schema.api_keys || {});
        setResponse(null);
        setTestError(null);
        setSelectedOperation(null);
        setParameters({});
        setError('');
        fetchOperations(schema._id, localStorage.getItem('access_token'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toolParam, schemas]);

    const openTool = (id) => setSearchParams(id ? { tool: id } : {});
    const backToList = () => { resetForm(); setSearchParams({}); };

    const handleSelectOperation = (operation) => {
        setSelectedOperation(operation);
        setResponse(null);
        setTestError(null);
        const initialParams = {};
        (operation?.parameters || []).forEach((param) => { initialParams[param.name] = ''; });
        setParameters(initialParams);
    };

    const handleSaveSchema = async () => {
        setSaving(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            // One entry per credential of the schema (what the field effect keeps), blank = keep the stored one.
            const keys = Object.fromEntries(credentials.map((field) => [field.key, apiKeys[field.key] || '']));
            const payload = { title, description, schema_content: schemaContent, api_keys: keys };
            if (selectedSchema) {
                await axios.put(`/api/ai-assistants/openapi-schemas/${selectedSchema._id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                showToast('success', t('dataToolsUi.tools.updated', 'Outil « {{title}} » enregistré.', { title }));
            } else {
                await axios.post('/api/ai-assistants/openapi-schemas', payload, { headers: { Authorization: `Bearer ${token}` } });
                showToast('success', t('dataToolsUi.tools.created', 'Outil « {{title}} » créé : ajoutez-le aux outils d’un agent.', { title }));
            }
            backToList();
            await fetchSchemas(token);
        } catch (saveError) {
            console.error('Failed to save schema:', saveError);
            const detail = saveError.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : selectedSchema
                ? t('dataToolsUi.tools.updateError', 'Impossible d’enregistrer cet outil.')
                : t('dataToolsUi.tools.createError', 'Impossible de créer cet outil.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSchema = async () => {
        if (!deleting) return;
        const schemaId = deleting._id;
        setDeleteBusy(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.delete(`/api/ai-assistants/openapi-schemas/${schemaId}`, { headers: { Authorization: `Bearer ${token}` } });
            await fetchSchemas(token);
            if (selectedSchema?._id === schemaId || toolParam === schemaId) backToList();
            showToast('success', t('dataToolsUi.tools.deleted', 'Outil « {{title}} » supprimé.', { title: deleting.title }));
        } catch (deleteError) {
            console.error('Failed to delete schema:', deleteError);
            showToast('danger', t('dataToolsUi.tools.deleteError', 'Impossible de supprimer cet outil.'));
        } finally {
            setDeleteBusy(false);
            setDeleting(null);
        }
    };

    const handleInvoke = async () => {
        if (!selectedOperation) return;
        const token = localStorage.getItem('access_token');
        setInvoking(true);
        setResponse(null);
        setTestError(null);
        try {
            const invokeResponse = await axios.post(
                '/api/ai-assistants/openapi-schemas/invoke',
                { operation_id: selectedOperation.operationId, parameters },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setResponse(invokeResponse.data);
        } catch (invokeError) {
            console.error('API invocation failed:', invokeError);
            setTestError(invokeError.response ? invokeError.response.data.detail : t('dataToolsUi.tools.unknownError', 'Erreur inconnue'));
        } finally {
            setInvoking(false);
        }
    };

    const isSaveDisabled = saving || !title.trim() || !schemaContent.trim();
    const loading = schemas === null;
    const list = schemas || [];
    const summary = toolsSummary(list, assistants);
    const attention = toolsAttention(list);

    const toastNode = toast && <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>{toast.text}</Toast>;
    const confirm = deleting && (
        <ConfirmDialog
            title={t('dataToolsUi.tools.deleteTitle', 'Supprimer l’outil « {{title}} » ?', { title: deleting.title })}
            message={t('dataToolsUi.tools.deleteMessage', 'Les agents qui l’utilisent ne pourront plus appeler ses opérations ; ses identifiants enregistrés sont effacés.')}
            confirmLabel={t('dataToolsUi.tools.deleteConfirm', 'Supprimer l’outil')}
            cancelLabel={t('common.cancel', 'Annuler')}
            busy={deleteBusy}
            onConfirm={handleDeleteSchema}
            onCancel={() => setDeleting(null)}
        />
    );

    // ------------------------------------------------------------------ a tool

    if (toolParam) {
        const editing = toolParam !== 'new';
        const notFound = editing && !loading && !list.some((s) => s._id === toolParam);
        const usedBy = selectedSchema ? toolUsage(selectedSchema, assistants) : [];
        const servers = toolServers({ schema_dict: parsedSchema });
        return (
            <div className="u-page oas-page">
                <PageHeader
                    breadcrumb={(
                        <>
                            <Link to="/openapi-schemes" onClick={(e) => { e.preventDefault(); backToList(); }}>{t('dataToolsUi.tools.title', 'API & outils')}</Link>
                            <ChevronRight aria-hidden="true" className="oas-crumb-sep" />
                            <span>{editing ? (selectedSchema?.title || '…') : t('dataToolsUi.tools.new', 'Nouvel outil')}</span>
                        </>
                    )}
                    title={editing ? (selectedSchema?.title || t('dataToolsUi.tools.title', 'API & outils')) : t('dataToolsUi.tools.new', 'Nouvel outil')}
                    subtitle={editing && selectedSchema
                        ? t('dataToolsUi.tools.toolSubtitle', '{{ops}} opérations appelables · modifié le {{date}}', { ops: (selectedSchema.function_definitions || []).length, date: formatShortDay(selectedSchema.updated_at) })
                        : t('dataToolsUi.tools.newSubtitle', 'Collez le schéma OpenAPI de l’API : chaque opération avec un operationId devient un outil de vos agents.')}
                    actions={(
                        <>
                            {editing && selectedSchema && (
                                <OverflowMenu label={t('dataToolsUi.more', 'Plus d’actions')} items={[
                                    { label: t('dataToolsUi.tools.deleteConfirm', 'Supprimer l’outil'), icon: Trash2, danger: true, onClick: () => setDeleting(selectedSchema) },
                                ]} />
                            )}
                            <button type="button" className="u-btn u-btn--secondary" onClick={backToList}>{t('common.cancel', 'Annuler')}</button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleSaveSchema} disabled={isSaveDisabled || notFound}>
                                {saving ? t('common.saving', 'Enregistrement…') : editing ? t('common.save', 'Enregistrer') : t('dataToolsUi.tools.create', 'Créer l’outil')}
                            </button>
                        </>
                    )}
                />
                <div className="u-well">
                    {error && <Banner tone="danger">{error}</Banner>}
                    {notFound ? (
                        <Card>
                            <EmptyState icon={FileCode} title={t('dataToolsUi.tools.notFound', 'Cet outil n’existe plus')}
                                action={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={backToList}>{t('dataToolsUi.tools.backToList', 'Retour aux outils')}</button>} />
                        </Card>
                    ) : (
                        <div className="oas-editor">
                            <div className="oas-editor__main">
                                <Card icon={Wrench} title={t('dataToolsUi.tools.definition', 'Définition')}
                                    description={t('dataToolsUi.tools.definitionDesc', 'Le nom et la consigne que l’agent lit pour décider d’appeler l’outil.')}>
                                    <div className="u-form">
                                        <Field label={t('dataToolsUi.tools.name', 'Nom de l’outil')} htmlFor="oas-title">
                                            <input id="oas-title" className="u-input" type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                                                placeholder={t('dataToolsUi.tools.namePlaceholder', 'ex. Commandes Shopify')} />
                                        </Field>
                                        <Field label={t('dataToolsUi.tools.agentDescription', 'Quand l’agent doit l’utiliser')} htmlFor="oas-description">
                                            <textarea id="oas-description" className="u-textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                                                placeholder={t('dataToolsUi.tools.agentDescriptionPlaceholder', 'ex. Retrouver une commande et ses expéditions avec le numéro de commande.')} />
                                        </Field>
                                    </div>
                                </Card>
                                <Card icon={CodeXml} title={t('dataToolsUi.tools.schema', 'Schéma OpenAPI')}
                                    badge={schemaContent.trim()
                                        ? (parsedSchema
                                            ? <Badge tone="success" dot>{t('dataToolsUi.tools.parsed', 'Lu')}</Badge>
                                            : <Badge tone="danger" dot>{t('dataToolsUi.tools.parseError', 'Illisible')}</Badge>)
                                        : <Badge>{t('dataToolsUi.tools.waiting', 'À coller')}</Badge>}
                                    description={t('dataToolsUi.tools.schemaDesc', 'OpenAPI 3, en YAML ou en JSON. L’adresse du serveur (servers) et un operationId par opération sont nécessaires.')}
                                    flush>
                                    <div className="oas-monaco">
                                        <Editor height="440px" language="yaml" theme="light" value={schemaContent} onChange={(value) => setSchemaContent(value || '')} options={editorOptions} />
                                    </div>
                                    {parsedSchema && (
                                        <div className="oas-schema-facts">
                                            <span><Server aria-hidden="true" /> {servers.length ? servers.join(' · ') : t('dataToolsUi.tools.noServer', 'Aucune adresse de serveur')}</span>
                                            <span><KeyRound aria-hidden="true" /> {t('dataToolsUi.tools.schemesCount', '{{count}} schémas d’authentification', { count: securitySchemes.length })}</span>
                                        </div>
                                    )}
                                </Card>
                                {credentials.length > 0 && (
                                    <Card icon={KeyRound} title={t('dataToolsUi.tools.credentials', 'Identifiants')}
                                        description={editing
                                            ? t('dataToolsUi.tools.credentialsEdit', 'Jamais réaffichés. Un champ laissé vide garde la valeur déjà enregistrée, s’il y en a une.')
                                            : t('dataToolsUi.tools.credentialsNew', 'Enregistrés avec l’outil et utilisés à chaque appel ; jamais réaffichés.')}>
                                        <div className="u-form-grid">
                                            {credentials.map((field) => (
                                                <Field key={field.key} htmlFor={`oas-cred-${field.key}`}
                                                    label={field.part === 'username'
                                                        ? t('dataToolsUi.tools.username', '{{scheme}} · identifiant', { scheme: field.scheme.name })
                                                        : field.part === 'password'
                                                            ? t('dataToolsUi.tools.password', '{{scheme}} · mot de passe', { scheme: field.scheme.name })
                                                            : `${field.scheme.name} · ${[field.scheme.type, field.scheme.scheme || field.scheme.in].filter(Boolean).join(' / ')}`}>
                                                    <input id={`oas-cred-${field.key}`} className="u-input" type={field.secret ? 'password' : 'text'} autoComplete={field.secret ? 'new-password' : 'off'}
                                                        value={apiKeys[field.key] || ''} onChange={(e) => setApiKeys({ ...apiKeys, [field.key]: e.target.value })}
                                                        placeholder={editing && field.secret ? t('dataToolsUi.tools.keepStored', 'Vide : valeur enregistrée conservée') : ''} />
                                                </Field>
                                            ))}
                                        </div>
                                    </Card>
                                )}
                            </div>
                            <div className="oas-editor__side">
                                {editing && selectedSchema && assistants && (
                                    <Card icon={Bot} title={t('dataToolsUi.tools.usedBy', 'Agents qui l’utilisent')} flush>
                                        {usedBy.length ? (
                                            <ul className="u-list">
                                                {usedBy.map((agent) => (
                                                    <li key={agent._id} className="u-row">
                                                        <div className="u-row__main"><div className="u-row__title">{agent.name}</div></div>
                                                        <div className="u-row__side">
                                                            <Link className="u-btn u-btn--ghost u-btn--sm" to={`/ai-assistants?assistantId=${encodeURIComponent(agent._id)}`}>
                                                                {t('dataToolsUi.open', 'Ouvrir')} <ChevronRight aria-hidden="true" />
                                                            </Link>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="oas-pad u-help">{t('dataToolsUi.tools.unused', 'Aucun agent ne l’utilise : ajoutez-le dans les outils d’un agent.')}</p>
                                        )}
                                    </Card>
                                )}
                                <Card icon={Play} title={t('dataToolsUi.tools.test', 'Tester une opération')}
                                    description={t('dataToolsUi.tools.testDesc', 'Un vrai appel à l’API, avec les identifiants enregistrés, avant de confier l’outil à un agent.')} flush>
                                    {!selectedSchema ? (
                                        <EmptyState icon={Play} title={t('dataToolsUi.tools.testAfterSave', 'Disponible après l’enregistrement')}>
                                            {t('dataToolsUi.tools.testAfterSaveHint', 'Les opérations se testent une fois le schéma enregistré.')}
                                        </EmptyState>
                                    ) : operations.length === 0 ? (
                                        <EmptyState icon={Zap} title={t('dataToolsUi.tools.noOperations', 'Aucune opération trouvée')}>
                                            {t('dataToolsUi.tools.noOperationsHint', 'Enregistrez un schéma valide avec un operationId par opération.')}
                                        </EmptyState>
                                    ) : (
                                        <>
                                            <ul className="u-list oas-ops" aria-label={t('dataToolsUi.tools.operations', 'Opérations')}>
                                                {operations.map((operation) => (
                                                    <li key={`${operation.method}:${operation.path}`}>
                                                        <button type="button" className={`oas-op${selectedOperation === operation ? ' is-selected' : ''}`}
                                                            aria-pressed={selectedOperation === operation} onClick={() => handleSelectOperation(operation)} disabled={!operation.operationId}>
                                                            <Badge tone={METHOD_TONE[(operation.method || 'get').toLowerCase()]}>{(operation.method || 'get').toUpperCase()}</Badge>
                                                            <span className="oas-op__text">
                                                                <span className="oas-op__path">{operation.path}</span>
                                                                <span className="oas-op__id">{operation.operationId || t('dataToolsUi.tools.noOperationId', 'sans operationId : non appelable')}</span>
                                                            </span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                            {selectedOperation && (
                                                <div className="oas-run">
                                                    <span className="u-label">{t('dataToolsUi.tools.parameters', 'Paramètres')}</span>
                                                    {(selectedOperation.parameters || []).length > 0 ? (
                                                        <div className="u-form">
                                                            {selectedOperation.parameters.map((param) => (
                                                                <Field key={param.name} htmlFor={`oas-param-${param.name}`}
                                                                    label={`${param.name}${param.required ? ' *' : ''}`}
                                                                    help={t('dataToolsUi.tools.paramIn', 'Dans : {{where}}', { where: param.in || 'query' })}>
                                                                    <input id={`oas-param-${param.name}`} className="u-input" type="text" value={parameters[param.name] || ''}
                                                                        onChange={(e) => setParameters((current) => ({ ...current, [param.name]: e.target.value }))} />
                                                                </Field>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="u-help">{t('dataToolsUi.tools.noParameters', 'Aucun paramètre.')}</p>
                                                    )}
                                                    <button type="button" className="u-btn u-btn--primary" onClick={handleInvoke} disabled={invoking}>
                                                        <Play aria-hidden="true" /> {invoking ? t('dataToolsUi.tools.running', 'Appel…') : t('dataToolsUi.tools.run', 'Lancer l’appel')}
                                                    </button>
                                                    {testError && (
                                                        <>
                                                            <Banner tone="danger">{t('dataToolsUi.tools.testFailed', 'L’appel a échoué.')}</Banner>
                                                            <pre className="oas-pre">{typeof testError === 'string' ? testError : JSON.stringify(testError, null, 2)}</pre>
                                                        </>
                                                    )}
                                                    {response && (
                                                        <>
                                                            <Banner tone="success">{t('dataToolsUi.tools.testOk', 'Réponse reçue.')}</Banner>
                                                            <pre className="oas-pre">{JSON.stringify(response, null, 2)}</pre>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Card>
                            </div>
                        </div>
                    )}
                </div>
                {confirm}
                {toastNode}
            </div>
        );
    }

    // ------------------------------------------------------------------ the list

    const subtitle = loading
        ? t('dataToolsUi.tools.subtitleLoading', 'Les API que vos agents IA peuvent appeler pendant une conversation.')
        : list.length === 0
            ? t('dataToolsUi.tools.subtitleEmpty', 'Les API que vos agents IA peuvent appeler pendant une conversation : collez un schéma OpenAPI pour en ajouter une.')
            : t('dataToolsUi.tools.subtitle', '{{tools}} outils · {{ops}} opérations appelables par vos agents', { tools: formatCount(summary.total), ops: formatCount(summary.operations) });

    return (
        <div className="u-page oas-page">
            <PageHeader
                title={t('dataToolsUi.tools.title', 'API & outils')}
                subtitle={subtitle}
                actions={(
                    <button type="button" className="u-btn u-btn--primary" onClick={() => openTool('new')}>
                        <Plus aria-hidden="true" /> {t('dataToolsUi.tools.new', 'Nouvel outil')}
                    </button>
                )}
            />
            <div className="u-well">
                {error && <Banner tone="danger">{error}</Banner>}
                {attention.length > 0 && (
                    <Card icon={TriangleAlert} iconTone="warning" title={t('dataToolsUi.attention.title', 'À vérifier')}
                        badge={<Badge tone="warning">{attention.length}</Badge>} flush className="oas-attention">
                        <ul className="u-list">
                            {attention.map((item) => (
                                <li key={item.key} className="u-row">
                                    <Badge tone="danger" dot>{t('dataToolsUi.attention.blocking', 'Bloquant')}</Badge>
                                    <div className="u-row__main">
                                        <div className="u-row__title">
                                            {item.kind === 'no_operations'
                                                ? t('dataToolsUi.tools.attNoOps', '« {{title}} » n’a aucune opération appelable', { title: item.schema.title })
                                                : t('dataToolsUi.tools.attNoServer', '« {{title}} » n’a pas d’adresse de serveur', { title: item.schema.title })}
                                        </div>
                                        <div className="u-row__meta">
                                            {item.kind === 'no_operations'
                                                ? t('dataToolsUi.tools.attNoOpsDetail', 'Ajoutez un operationId à chaque opération du schéma : sans lui, l’agent ne peut pas l’appeler.')
                                                : t('dataToolsUi.tools.attNoServerDetail', 'Ajoutez « servers » au schéma : sans adresse, chaque appel échoue.')}
                                        </div>
                                    </div>
                                    <div className="u-row__side">
                                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openTool(item.schema._id)}>
                                            {t('dataToolsUi.tools.fix', 'Corriger')} <ChevronRight aria-hidden="true" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </Card>
                )}
                {loading ? (
                    <div className="u-kpi-row oas-kpis" aria-hidden="true">{[0, 1, 2].map((i) => <span key={i} className="u-skeleton oas-kpi-skeleton" />)}</div>
                ) : list.length > 0 && (
                    <div className="u-kpi-row oas-kpis">
                        <Kpi icon={Wrench} label={t('dataToolsUi.tools.kpiTools', 'Outils')} value={formatCount(summary.total)}
                            context={attention.length
                                ? t('dataToolsUi.tools.kpiToolsBroken', '{{count}} à corriger', { count: attention.length })
                                : t('dataToolsUi.tools.kpiToolsOk', 'tous appelables')}
                            contextTone={attention.length ? 'danger' : undefined} />
                        <Kpi icon={Zap} label={t('dataToolsUi.tools.kpiOps', 'Opérations')} value={formatCount(summary.operations)}
                            context={t('dataToolsUi.tools.kpiOpsContext', 'avec un operationId : appelables par l’agent')} />
                        {summary.used !== null && (
                            <Kpi icon={Bot} tone={summary.used ? 'success' : 'neutral'} label={t('dataToolsUi.tools.kpiUsed', 'Utilisés par un agent')}
                                value={formatCount(summary.used)} unit={`/ ${formatCount(summary.total)}`}
                                context={t('dataToolsUi.tools.kpiUsedContext', '{{count}} agents équipés', { count: summary.agents })}
                                meter={{ value: summary.used, max: summary.total, tone: 'success', label: t('dataToolsUi.tools.kpiUsed', 'Utilisés par un agent') }} />
                        )}
                    </div>
                )}
                <Card icon={FileCode} title={t('dataToolsUi.tools.listTitle', 'Vos outils')}
                    description={t('dataToolsUi.tools.listDesc', 'Ouvrez un outil pour modifier son schéma, ses identifiants ou tester une opération.')} flush>
                    {loading ? (
                        <div className="oas-skeleton" aria-busy="true">{[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}</div>
                    ) : list.length === 0 ? (
                        <EmptyState icon={FileCode} title={t('dataToolsUi.tools.emptyTitle', 'Aucun outil pour l’instant')}
                            action={<button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => openTool('new')}><Plus aria-hidden="true" /> {t('dataToolsUi.tools.new', 'Nouvel outil')}</button>}>
                            {t('dataToolsUi.tools.emptyHint', 'Collez le schéma OpenAPI d’une API (votre boutique, votre ERP, un service interne) : l’agent pourra l’appeler pendant ses réponses.')}
                        </EmptyState>
                    ) : (
                        <ul className="u-list">
                            {list.map((schema) => {
                                const ops = (schema.function_definitions || []).length;
                                const used = assistants ? toolUsage(schema, assistants) : null;
                                const servers = toolServers(schema);
                                const meta = [schema.description || t('dataToolsUi.tools.noDescription', 'Sans consigne pour l’agent'), servers[0], schema.updated_at ? t('dataToolsUi.tools.updatedOn', 'modifié le {{date}}', { date: formatShortDay(schema.updated_at) }) : null]
                                    .filter(Boolean).join(' · ');
                                return (
                                    <li key={schema._id} className="u-row">
                                        <span className="u-icon-chip u-icon-chip--neutral" aria-hidden="true"><FileCode strokeWidth={1.8} /></span>
                                        <div className="u-row__main">
                                            <div className="u-row__title">
                                                {schema.title}
                                                <Badge tone={ops ? undefined : 'danger'}>{t('dataToolsUi.tools.opsBadge', '{{count}} opérations', { count: ops })}</Badge>
                                                {used && (used.length
                                                    ? <Badge tone="success" dot>{t('dataToolsUi.tools.usedBadge', '{{count}} agents', { count: used.length })}</Badge>
                                                    : <Badge>{t('dataToolsUi.tools.unusedBadge', 'Aucun agent')}</Badge>)}
                                            </div>
                                            <div className="u-row__meta" title={meta}>{meta}</div>
                                        </div>
                                        <div className="u-row__side">
                                            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openTool(schema._id)}>
                                                {t('dataToolsUi.open', 'Ouvrir')} <ChevronRight aria-hidden="true" />
                                            </button>
                                            <OverflowMenu label={t('dataToolsUi.tools.moreFor', 'Plus d’actions pour {{title}}', { title: schema.title })} items={[
                                                { label: t('dataToolsUi.tools.deleteConfirm', 'Supprimer l’outil'), icon: Trash2, danger: true, onClick: () => setDeleting(schema) },
                                            ]} />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>
            </div>
            {confirm}
            {toastNode}
        </div>
    );
};

export default OpenAPISchemaManagement;
